import { useTheme } from "@shopify/restyle";
import { debounce } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { RefObject } from "react";
import { Platform, TextStyle } from "react-native";
import {
  NativeSyntheticEvent,
  TextInput,
  TextInputContentSizeChangeEventData,
} from "react-native";

import {
  Input as PrimitiveInput,
  PrimativeInputProps,
} from "../../primitive/Input/Input";

type PaddingToken = NonNullable<PrimativeInputProps<"text">["paddingVertical"]>;

type SizeStyles = {
  paddingVertical: PaddingToken;
  paddingHorizontal: PaddingToken;
  minHeight: number;
};

type TextAreaFocusEvent = Parameters<
  NonNullable<PrimativeInputProps<"text">["onFocus"]>
>[0];

export type TextAreaSize = "tiny" | "small" | "regular" | "large";

export type TextAreaProps = {
  name: string;
  accessibilityLabel?: string;
  placeholder?: string;
  value?: string;
  onChange: (text: string) => void;
  testID?: string;
  isDisabled?: boolean;
  isViewOnly?: boolean;
  /** Use inside a parent control that supplies the border and focus treatment. */
  isBorderless?: boolean;
  hasError?: boolean;
  size?: TextAreaSize;
  inputRef?: RefObject<TextInput | null>;
  onBlur?: (e: TextAreaFocusEvent) => void;
  onFocus?: (e: TextAreaFocusEvent) => void;
  onContentSizeChange?: () => void;
  maxLength?: number;
  isRequired?: boolean;
};

const getSizeStyles = (size: TextAreaSize): SizeStyles => {
  switch (size) {
    case "tiny":
      return {
        paddingHorizontal: 2,
        paddingVertical: 2,
        minHeight: 56,
      };
    case "small":
      return {
        paddingVertical: 3,
        paddingHorizontal: 3,
        minHeight: 64,
      };
    case "large":
      return {
        paddingVertical: 5,
        paddingHorizontal: 5,
        minHeight: 80,
      };
    case "regular":
    default:
      return {
        paddingVertical: 4,
        paddingHorizontal: 4,
        minHeight: 72,
      };
  }
};

export const TextArea = ({
  name,
  accessibilityLabel,
  placeholder,
  value,
  onChange,
  testID,
  isDisabled,
  isViewOnly,
  isBorderless = false,
  hasError,
  size = "regular",
  inputRef,
  onBlur,
  onFocus,
  onContentSizeChange,
  maxLength,
  isRequired,
}: TextAreaProps) => {
  const theme = useTheme();
  const [isFocused, setIsFocused] = useState(false);
  const [contentHeight, setContentHeight] = useState(0);
  const debouncedSetContentHeight = useMemo(
    () => debounce(setContentHeight),
    []
  );

  useEffect(() => {
    if (value === "") {
      // Web scrollHeight includes the assigned height even after the text clears.
      debouncedSetContentHeight.cancel();
      setContentHeight(0);
    }
  }, [debouncedSetContentHeight, value]);

  useEffect(
    () => () => debouncedSetContentHeight.cancel(),
    [debouncedSetContentHeight]
  );

  const isEditable = !isDisabled && !isViewOnly;

  const { paddingVertical, paddingHorizontal, minHeight } = getSizeStyles(size);

  const dynamicHeight = Math.max(minHeight, contentHeight);

  const handleContentSizeChange = (
    e: NativeSyntheticEvent<TextInputContentSizeChangeEventData>
  ) => {
    onContentSizeChange?.();
    debouncedSetContentHeight(e.nativeEvent.contentSize.height);
  };

  const textColor = isDisabled
    ? "text-disabled"
    : hasError
    ? "text-error"
    : "text-primary";

  const borderColor = hasError
    ? "outline-status-error"
    : isFocused
    ? "grayscale-1000"
    : "grayscale-500";

  const bgColor = isDisabled
    ? "grayscale-500"
    : isViewOnly
    ? "surface-secondary"
    : "surface-primary";

  const styleProps =
    Platform.OS === "web"
      ? { outlineWidth: 0, outlineStyle: "none", overflow: "hidden" }
      : {};

  return (
    <PrimitiveInput<"text">
      name={name}
      accessibilityLabel={accessibilityLabel}
      type="text"
      testID={testID ?? "text-area"}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      maxLength={maxLength}
      aria-required={isRequired}
      multiline
      textAlignVertical="top"
      paddingVertical={paddingVertical}
      paddingHorizontal={paddingHorizontal}
      width="100%"
      color={textColor}
      placeholderTextColor={theme.colors["text-secondary"]}
      lineHeight={16}
      fontSize={16}
      letterSpacing={16 * -0.02}
      fontFamily={
        Platform.OS === "web" ? "Inter_600SemiBold, Inter" : "Inter_600SemiBold"
      }
      editable={isEditable}
      aria-disabled={isDisabled}
      focusable={isEditable}
      inputRef={inputRef}
      backgroundColor={bgColor}
      minHeight={minHeight}
      height={dynamicHeight}
      onContentSizeChange={handleContentSizeChange}
      style={{ ...styleProps } as TextStyle}
      onFocus={(e) => {
        if (isEditable) {
          setIsFocused(true);
          onFocus?.(e);
        }
      }}
      onBlur={(e) => {
        setIsFocused(false);
        onBlur?.(e);
      }}
      borderRadius="2"
      borderWidth={isBorderless ? 0 : 1.5}
      borderColor={borderColor}
    />
  );
};
